import { IPolyPizzaService, PolyPizzaSearchParams, PolyPizzaSearchResponse, PolyPizzaAssetDetails, PolyPizzaAsset } from './types';
import { PolyPizzaAdapter } from '@/infrastructure/adapters/PolyPizzaAdapter';

export class PolyPizzaService implements IPolyPizzaService {
  constructor(private readonly adapter: PolyPizzaAdapter) {}

  async searchAssets(params: PolyPizzaSearchParams): Promise<PolyPizzaSearchResponse> {
    try {
      // Build URL path and query params
      // Poly Pizza API: keyword is a PATH param (/search/{keyword}), not query param
      const { path, query } = this.adapter.buildSearchUrl(params);
      
      // Make request with keyword in path
      const response = await this.adapter.get<unknown>(path, query);
      
      return this.normalizeSearchResponse(response, params);
    } catch (error) {
      console.error('Error searching Poly Pizza assets:', error);
      console.error('Error details:', error instanceof Error ? error.message : String(error));
      // Return empty response on error
      return {
        assets: [],
        total: 0,
        page: params.page || 1,
        limit: params.limit || 20,
        hasMore: false,
      };
    }
  }

  async searchPage(params: PolyPizzaSearchParams): Promise<PolyPizzaSearchResponse> {
    const { path, query } = this.adapter.buildSearchUrl(params);
    const response = await this.adapter.get<unknown>(path, query);
    return this.normalizeSearchResponse(response, params);
  }

  async downloadFile(url: string): Promise<Blob> {
    return this.adapter.getBlob(url);
  }

  async getPopular(limit: number = 20): Promise<PolyPizzaSearchResponse> {
    try {
      const response = await this.adapter.get<unknown>('/v1/popular', { Limit: limit });
      
      return this.normalizeSearchResponse(response, { limit });
    } catch (error) {
      console.error('Error fetching popular Poly Pizza assets:', error);
      return {
        assets: [],
        total: 0,
        page: 1,
        limit: limit,
        hasMore: false,
      };
    }
  }

  async getAssetDetails(assetId: string): Promise<PolyPizzaAssetDetails> {
    try {
      // Poly Pizza model details endpoint
      const response = await this.adapter.get<Record<string, unknown>>(`/model/${assetId}`);
      
      return this.normalizeAssetDetails(response);
    } catch (error) {
      console.error('Error fetching Poly Pizza asset details:', error);
      throw error;
    }
  }

  async downloadAsset(assetId: string): Promise<Blob> {
    try {
      // First get asset details to find download URL
      const assetDetails = await this.getAssetDetails(assetId);
      
      if (!assetDetails.downloadUrl) {
        throw new Error('Download URL not available for this asset');
      }

      // Download the asset file through getExternalContent, on the user's bearer
      return await this.adapter.getBlob(assetDetails.downloadUrl);
    } catch (error) {
      console.error('Error downloading Poly Pizza asset:', error);
      throw error;
    }
  }

  // Normalize search response to match our interface
  private normalizeSearchResponse(response: unknown, params: PolyPizzaSearchParams): PolyPizzaSearchResponse {
    const data = response as Record<string, unknown>;
    
    // Poly Pizza API returns: { results: [...], total, page, etc. }
    // API pages are 0-indexed, but we use 1-based pages internally
    if (data.results && Array.isArray(data.results)) {
      const results = data.results as unknown[];
      const apiPage = (data.page as number) ?? 0; // API returns 0-indexed
      const currentPage = params.page ?? 1; // Our internal 1-based page
      const limit = params.limit || 20;
      const total = (data.total as number) || results.length;
      
      return {
        assets: results.map((item, index) => 
          this.normalizeAsset(item as Record<string, unknown>, index)
        ),
        total,
        page: currentPage,
        limit,
        hasMore: !!(data.next as string) || ((apiPage + 1) * limit < total),
      };
    }

    // Try alternative response structure with 'models' array
    if (data.models && Array.isArray(data.models)) {
      const models = data.models as unknown[];
      return {
        assets: models.map((item, index) => this.normalizeAsset(item as Record<string, unknown>, index)),
        total: (data.total as number) || models.length,
        page: params.page || 1,
        limit: params.limit || 20,
        hasMore: false,
      };
    }

    // Try direct array response
    if (Array.isArray(data)) {
      return {
        assets: data.map((item, index) => this.normalizeAsset(item as Record<string, unknown>, index)),
        total: data.length,
        page: params.page || 1,
        limit: params.limit || 20,
        hasMore: false,
      };
    }

    // If there's a single model in the response (from search with single result)
    if (data.ID || data.id) {
      const asset = this.normalizeAsset(data, 0);
      return {
        assets: [asset],
        total: 1,
        page: 1,
        limit: params.limit || 20,
        hasMore: false,
      };
    }

    console.warn('Unknown response structure:', data);
    return {
      assets: [],
      total: 0,
      page: params.page || 1,
      limit: params.limit || 20,
      hasMore: false,
    };
  }

  // Normalize asset data to match our interface
  private normalizeAsset(data: Record<string, unknown>, index?: number): PolyPizzaAsset {
    // Poly Pizza model structure (based on pizzabox package and OpenAPI docs):
    // { ID, Title, Description, Thumbnail, Download, Author, Licence, Animated, Rigged, "Tri Count", etc. }
    
    const id = data.ID || data.id;
    const uniqueId = id ? String(id) : (index !== undefined ? `poly-pizza-${index}` : `poly-pizza-${Date.now()}-${Math.random()}`);
    
    // Extract thumbnail URL
    const thumbnailUrl = data.Thumbnail || data.thumbnail || data.thumbnailUrl;
    
    // Extract download URL - Poly Pizza provides direct GLB/GLTF downloads
    const downloadUrl = data.Download || data.download || data.downloadUrl;
    
    // Determine license type (CC0 or CC-BY)
    const licenseRaw = data.Licence || data.License || data.licence || data.license;
    const license: 'CC0' | 'CC-BY' = String(licenseRaw).toUpperCase().includes('CC0') ? 'CC0' : 'CC-BY';
    
    // Extract author info
    const authorData = data.Author || data.author;
    let author: { id: string; name: string } | undefined;
    if (typeof authorData === 'string') {
      author = { id: '', name: authorData };
    } else if (authorData && typeof authorData === 'object') {
      const authorObj = authorData as Record<string, unknown>;
      author = {
        id: String(authorObj.ID || authorObj.id || ''),
        name: String(authorObj.Name || authorObj.name || authorObj.username || ''),
      };
    }
    
    // Build attribution string for CC-BY assets (with or without author)
    const assetTitle = String(data.Title || data.title || data.name || 'Model');
    const attribution = license === 'CC-BY'
      ? author
        ? `"${assetTitle}" by ${author.name} is licensed under CC-BY`
        : `"${assetTitle}" is licensed under CC-BY`
      : undefined;

    // Extract animated/rigged info (Poly Pizza has these assets, especially in People & Characters)
    // Check for various possible field names
    const animated = data.Animated || data.animated || data.IsAnimated || data.isAnimated;
    const rigged = data.Rigged || data.rigged || data.IsRigged || data.isRigged;
    const isAnimated = animated !== undefined ? Boolean(Number(animated) || animated === true || String(animated).toLowerCase() === 'true') : undefined;
    const isRigged = rigged !== undefined ? Boolean(Number(rigged) || rigged === true || String(rigged).toLowerCase() === 'true') : undefined;

    // Extract triangle count (Poly Pizza OpenAPI: `Tri Count`)
    const triRaw =
      (data['Tri Count' as keyof typeof data] as unknown) ??
      (data.TriCount as unknown) ??
      (data.triCount as unknown);

    let triCount: number | undefined;
    if (triRaw !== undefined && triRaw !== null) {
      const parsed = Number(triRaw);
      if (Number.isFinite(parsed) && parsed >= 0) {
        triCount = parsed;
      }
    }

    return {
      id: uniqueId,
      title: String(data.Title || data.title || data.name || data.Name || 'Untitled'),
      description: data.Description ? String(data.Description) : (data.description ? String(data.description) : undefined),
      thumbnailUrl: thumbnailUrl ? String(thumbnailUrl) : undefined,
      previewUrl: thumbnailUrl ? String(thumbnailUrl) : undefined,
      downloadUrl: downloadUrl ? String(downloadUrl) : undefined,
      license,
      category: data.Category ? String(data.Category) : (data.category ? String(data.category) : undefined),
      tags: Array.isArray(data.Tags) 
        ? (data.Tags as unknown[]).map(t => String(t)) 
        : (Array.isArray(data.tags) ? (data.tags as unknown[]).map(t => String(t)) : undefined),
      fileFormat: 'glb', // Poly Pizza primarily provides GLB format
      author,
      attribution,
      isAnimated,
      isRigged,
      triCount,
    };
  }

  // Normalize asset details
  private normalizeAssetDetails(data: unknown): PolyPizzaAssetDetails {
    const details = data as Record<string, unknown>;
    const normalized = this.normalizeAsset(details);

    return {
      ...normalized,
      metadata: details.metadata as Record<string, unknown> | undefined,
    };
  }
}
